// ** UI
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

// ** Components
import Section from '@/components/common/Section';
import LinkRowCard from './LinkRowCard';

// ** Types
import type { StrapiComponentProps } from '@/types/strapi';
import type {
	ComponentElementsButtonLinkCard,
	ComponentBlocksButtonLinkRow as ButtonLinkRowProps
} from '@/types/graphql';

type ComponentBlocksButtonLinkRowProps = StrapiComponentProps<ButtonLinkRowProps>;

const ComponentBlocksButtonLinkRow = ({
	linkCard,
	providers,
	currentUser,
	containerSx,
	buttonLinkRowListAriaLabel
}: ComponentBlocksButtonLinkRowProps) => {
	const theme = useTheme();
	if (!linkCard || linkCard?.length === 0) return null;

	const count = linkCard.length;
	const useFixedDesktopRowWidth = count === 2 || count === 3;
	const ariaLabel = buttonLinkRowListAriaLabel || 'Navigation Links';

	// 5 items: 6-col subgrid so last row of 2 centers by spanning 3 cols each
	const useSixColGrid = count === 5;
	return (
		<Section containerSx={containerSx} backgroundColor={theme.palette.grey[600]}>
			<Box
				component="ul"
				aria-label={ariaLabel}
				sx={{
					display: 'grid',
					gap: '1rem',
					width: '100%',
					maxWidth: { sm: useFixedDesktopRowWidth ? '956px' : 'none' },
					mx: { sm: useFixedDesktopRowWidth ? 'auto' : undefined },
					listStyle: 'none',
					m: 0,
					p: 0,
					gridTemplateColumns: {
						xs: 'repeat(2, 1fr)',
						sm: count <= 4 ? `repeat(${count}, 1fr)` : useSixColGrid ? 'repeat(6, 1fr)' : 'repeat(3, 1fr)'
					}
				}}
			>
				{linkCard
					?.filter((item): item is ComponentElementsButtonLinkCard => !!item)
					.filter(({ active }) => active)
					.filter(({ visible }) => {
						if (visible === 'all') return true;
						if (visible === 'authenticated') return !!currentUser;
						if (visible === 'guests') return !currentUser;
						return false;
					})
					.map((card, i) => {
						const isOddCount = count % 2 !== 0;

						return (
							<Box
								key={`linkRow-${card.title}-${card.id}`}
								component="li"
								sx={{
									minWidth: 0,
									gridColumn: {
										xs: isOddCount && i === 0 ? 'span 2' : 'span 1',
										sm: useSixColGrid ? (i < 3 ? 'span 2' : 'span 3') : 'span 1'
									}
								}}
							>
								<LinkRowCard {...card} providers={providers} />
							</Box>
						);
					})}
			</Box>
		</Section>
	);
};

export default ComponentBlocksButtonLinkRow;
